/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Two-step group-create modal (Figma Ping Desktop 4036:14066 / 14178 /
// 14284 / 14266):
//   Step 1 "select"  — search + alphabetical contact list with checkboxes
//                       and a horizontal strip of selected-member chips.
//                       Footer: Cancel · Next.
//   Step 2 "details" — group avatar picker + name input (0/50) +
//                       participants strip + "Add to folder". Footer:
//                       Cancel (← back) · Done.
// Host supplies search + MLS orchestration via callbacks.

import { Check, ChevronRight, Plus, Search, X } from "lucide-react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	Image,
	Modal,
	Pressable,
	ScrollView,
	StyleSheet,
	Text,
	TextInput,
	View,
} from "react-native";
import { MAX_GROUP_PEER_ACCOUNTS } from "../conversation/groupConversation";
import { Avatar } from "../components/Avatar";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { NewChatIcon } from "./NewChatIcon";
import { WEB_INPUT_OUTLINE_RESET } from "../foundations/webInputStyle";
import {
	getCurrentUserAccountId,
	getCurrentUserDisplayName,
} from "../sync/registerCurrentUser";
import type { NewChatModalDirectoryUser } from "./NewChatModal";

export type { NewChatModalDirectoryUser };

const MAX_NAME = 50;

export interface NewGroupModalProps {
	readonly open: boolean;
	readonly onDismiss: () => void;
	readonly onSearch: (
		query: string,
		signal: AbortSignal,
	) => Promise<readonly NewChatModalDirectoryUser[]>;
	readonly onCreate: (params: {
		readonly title: string;
		readonly members: readonly NewChatModalDirectoryUser[];
		readonly avatarUri?: string;
	}) => Promise<string>;
	readonly onConversationStarted: (conversationId: string) => void;
	readonly maxPeers?: number;
	/** Currently-picked group avatar (data/remote URL). */
	readonly groupAvatarUri?: string;
	/** Open the platform image picker for the group avatar. */
	readonly onPickGroupAvatar?: () => void;
	/** The current user's own account id — excluded from the search list (you are
	 *  the group creator and always a member, so you can't add yourself). Defaults
	 *  to the registered current-user id; provide to override (e.g. tests). */
	readonly selfUserId?: string | undefined;
	/** The current user's display name, shown on the pinned, non-removable "You"
	 *  chip that represents your implicit membership. Defaults to the registered
	 *  current-user display name. */
	readonly selfDisplayName?: string | undefined;
	/** The current user's own avatar (a resolved image URL). When set, the "You"
	 *  chip renders the real picture instead of just the initial. */
	readonly selfAvatarUri?: string | undefined;
}

export function NewGroupModal(props: NewGroupModalProps): JSX.Element | null {
	const {
		open,
		onDismiss,
		onSearch,
		onCreate,
		onConversationStarted,
		maxPeers = MAX_GROUP_PEER_ACCOUNTS,
		groupAvatarUri,
		onPickGroupAvatar,
		selfUserId,
		selfDisplayName,
		selfAvatarUri,
	} = props;

	// The current user is the group creator and always a member — you can't add
	// yourself, so your own account is excluded from the search list and instead
	// shown as a pinned, non-removable "You" chip in the selected strip.
	const selfId = selfUserId ?? getCurrentUserAccountId();
	const selfName = selfDisplayName ?? getCurrentUserDisplayName() ?? "You";

	const [step, setStep] = useState<"select" | "details">("select");
	const [groupTitle, setGroupTitle] = useState("");
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<readonly NewChatModalDirectoryUser[]>([]);
	const [selected, setSelected] = useState<readonly NewChatModalDirectoryUser[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const selectedIds = useMemo(() => new Set(selected.map((u) => u.id)), [selected]);

	// Group the directory results into alphabetical sections (Figma A, B…).
	// The current user is filtered out — they're always in the group and must not
	// appear as a pickable row.
	const sections = useMemo(() => {
		const buckets = new Map<string, NewChatModalDirectoryUser[]>();
		for (const u of results) {
			if (selfId && u.id === selfId) continue;
			const letter = (u.displayName.trim().charAt(0) || "#").toUpperCase();
			const key = /[A-Z]/.test(letter) ? letter : "#";
			const arr = buckets.get(key);
			if (arr) arr.push(u);
			else buckets.set(key, [u]);
		}
		return Array.from(buckets.entries())
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([letter, users]) => ({ letter, users }));
	}, [results, selfId]);

	useEffect(() => {
		if (!open || step !== "select") return;
		const controller = new AbortController();
		abortRef.current?.abort();
		abortRef.current = controller;
		const t = setTimeout(async () => {
			setIsLoading(true);
			setError(null);
			try {
				const users = await onSearch(query, controller.signal);
				if (!controller.signal.aborted) setResults(users);
			} catch (err) {
				if (controller.signal.aborted) return;
				const serverMessage = (err as { serverMessage?: string } | null)?.serverMessage;
				setError(serverMessage ?? "Couldn't search the directory. Try again.");
			} finally {
				if (!controller.signal.aborted) setIsLoading(false);
			}
		}, 450);
		return () => {
			controller.abort();
			clearTimeout(t);
		};
	}, [query, open, step, onSearch]);

	useEffect(() => {
		if (!open) {
			setStep("select");
			setGroupTitle("");
			setQuery("");
			setResults([]);
			setSelected([]);
			setError(null);
			setIsCreating(false);
		}
	}, [open]);

	const toggleMember = (user: NewChatModalDirectoryUser): void => {
		if (selectedIds.has(user.id)) {
			setSelected((prev) => prev.filter((u) => u.id !== user.id));
			return;
		}
		if (selected.length >= maxPeers) {
			setError(`You can add at most ${maxPeers} people.`);
			return;
		}
		setError(null);
		setSelected((prev) => [...prev, user]);
	};

	const handleCreate = async (): Promise<void> => {
		if (selected.length < 1) {
			setError("Add at least one person to create a group.");
			return;
		}
		setIsCreating(true);
		setError(null);
		try {
			const conversationId = await onCreate({
				title: groupTitle.trim(),
				members: selected,
				...(groupAvatarUri !== undefined ? { avatarUri: groupAvatarUri } : {}),
			});
			onConversationStarted(conversationId);
			onDismiss();
		} catch (err) {
			const serverMessage = (err as { serverMessage?: string } | null)?.serverMessage;
			const message =
				err instanceof Error && err.message.length > 0
					? err.message
					: serverMessage && serverMessage.length > 0
						? serverMessage
						: "Couldn't create that group. Try again.";
			setError(message);
			setIsCreating(false);
		}
	};

	if (!open) return null;

	return (
		<Modal visible={open} transparent animationType="none" onRequestClose={onDismiss}>
			<Pressable
				accessibilityRole="none"
				onPress={onDismiss}
				style={StyleSheet.absoluteFill}
				className="z-50 flex-1 items-center justify-center px-md bg-black/60"
			>
				<Pressable
					onPress={() => undefined}
					className="
						w-full max-w-[500px] max-h-[85vh] flex flex-col
						rounded-[12px] border
						bg-light-surface-primary border-light-border-secondary
						dark:bg-[#15171C] dark:border-[#22242A]
						shadow-2xl
					"
				>
					{/* Header */}
					<View className="flex flex-row items-center justify-between px-[29px] pt-5 pb-2">
						<Text className="m-0 font-sans font-bold text-[20px] leading-7 text-light-text-primary dark:text-white">
							New group
						</Text>
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Close"
							onPress={onDismiss}
							className="w-[26px] h-[26px] rounded-[8px] flex items-center justify-center hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]"
						>
							<X size={18} strokeWidth={2} color="#B5BAC3" />
						</Pressable>
					</View>

					{step === "select" ? (
						<SelectStep
							query={query}
							onQueryChange={setQuery}
							sections={sections}
							selected={selected}
							selectedIds={selectedIds}
							selfName={selfName}
							{...(selfAvatarUri !== undefined ? { selfAvatarUri } : {})}
							isLoading={isLoading}
							error={error}
							onToggle={toggleMember}
							onRemove={(id) => setSelected((p) => p.filter((u) => u.id !== id))}
						/>
					) : (
						<DetailsStep
							groupTitle={groupTitle}
							onTitleChange={(t) => setGroupTitle(t.slice(0, MAX_NAME))}
							selected={selected}
							selfName={selfName}
							{...(selfAvatarUri !== undefined ? { selfAvatarUri } : {})}
							error={error}
							{...(groupAvatarUri !== undefined ? { groupAvatarUri } : {})}
							{...(onPickGroupAvatar !== undefined ? { onPickGroupAvatar } : {})}
						/>
					)}

					{/* Footer */}
					<View className="flex flex-row items-center gap-3 px-[29px] py-4 border-t border-light-border-secondary dark:border-[#22242A]">
						<Pressable
							accessibilityRole="button"
							onPress={() => (step === "details" ? setStep("select") : onDismiss())}
							className="flex-1 h-9 rounded-[8px] items-center justify-center border border-light-border-secondary dark:border-[#2F333B]"
						>
							<Text className="font-sans text-[14px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
								Cancel
							</Text>
						</Pressable>
						{step === "select" ? (
							<Pressable
								accessibilityRole="button"
								disabled={selected.length < 1}
								onPress={() => setStep("details")}
								className="flex-1 h-9 rounded-[8px] items-center justify-center bg-[#537EFF] disabled:opacity-40"
							>
								<Text className="font-sans text-[14px] font-semibold text-white">Next</Text>
							</Pressable>
						) : (
							<Pressable
								accessibilityRole="button"
								disabled={isCreating || selected.length < 1}
								onPress={() => void handleCreate()}
								className="flex-1 h-9 rounded-[8px] items-center justify-center bg-[#537EFF] disabled:opacity-40"
							>
								<Text className="font-sans text-[14px] font-semibold text-white">
									{isCreating ? "Creating…" : "Done"}
								</Text>
							</Pressable>
						)}
					</View>
				</Pressable>
			</Pressable>
		</Modal>
	);
}

// ── Step 1: member selection ────────────────────────────────────────
function SelectStep({
	query,
	onQueryChange,
	sections,
	selected,
	selectedIds,
	selfName,
	selfAvatarUri,
	isLoading,
	error,
	onToggle,
	onRemove,
}: {
	query: string;
	onQueryChange: (v: string) => void;
	sections: ReadonlyArray<{ letter: string; users: readonly NewChatModalDirectoryUser[] }>;
	selected: readonly NewChatModalDirectoryUser[];
	selectedIds: ReadonlySet<string>;
	selfName: string;
	selfAvatarUri?: string;
	isLoading: boolean;
	error: string | null;
	onToggle: (u: NewChatModalDirectoryUser) => void;
	onRemove: (id: string) => void;
}): JSX.Element {
	return (
		<>
			<View className="px-[29px] pb-2">
				<View className="flex flex-row items-center h-8 px-sm gap-sm rounded-[8px] bg-light-surface-secondary dark:bg-[#111318]">
					<Search size={16} strokeWidth={2} color="#6B7280" />
					<TextInput
						value={query}
						onChangeText={onQueryChange}
						placeholder="Search…"
						placeholderTextColor="#6B7280"
						autoCapitalize="none"
						autoCorrect={false}
						autoFocus
						className="flex-1 font-sans text-[12px] text-light-text-primary dark:text-white"
						style={WEB_INPUT_OUTLINE_RESET}
					/>
				</View>
			</View>

			{/* Selected-member strip (Figma `Selection`). The current user is always
			    a member (they're creating the group), shown as a pinned, non-removable
			    "You" chip first, so the strip renders even before anyone else is added. */}
			<ScrollView
				horizontal
				showsHorizontalScrollIndicator={false}
				className="px-[29px] pb-2 max-h-[72px]"
			>
				<View className="flex flex-row gap-3">
					<View className="w-10 items-center gap-1">
						<Avatar
							initials={selfName.charAt(0).toUpperCase() || "?"}
							{...(selfAvatarUri !== undefined ? { imageUri: selfAvatarUri } : {})}
							size="medium"
							shape="rounded"
						/>
						<Text
							numberOfLines={1}
							className="font-sans text-[12px] leading-[17px] text-light-text-secondary dark:text-[#B5BAC3] max-w-10"
						>
							You
						</Text>
					</View>
					{selected.map((u) => (
							<View key={u.id} className="w-10 items-center gap-1">
								<View className="relative">
									<Avatar
										initials={u.displayName.charAt(0).toUpperCase() || "?"}
										{...(u.avatarUri !== undefined ? { imageUri: u.avatarUri } : {})}
										size="medium"
										shape="rounded"
									/>
									<Pressable
										accessibilityRole="button"
										accessibilityLabel={`Remove ${u.displayName}`}
										onPress={() => onRemove(u.id)}
										className="absolute -top-1 -right-1 w-4 h-4 rounded-full items-center justify-center bg-[#0A0B0F] border border-[#2F333B]"
									>
										<X size={10} strokeWidth={2.5} color="#E8EAED" />
									</Pressable>
								</View>
								<Text
									numberOfLines={1}
									className="font-sans text-[12px] leading-[17px] text-light-text-secondary dark:text-[#B5BAC3] max-w-10"
								>
									{u.displayName.split(" ")[0]}
								</Text>
							</View>
						))}
				</View>
			</ScrollView>

			<ScrollView accessibilityLiveRegion="polite" className="flex-1 min-h-0 px-[29px] pb-2">
				{error !== null && (
					<Text className="font-sans text-[12px] text-light-accent-rose px-sm py-sm m-0">
						{error}
					</Text>
				)}
				{isLoading && sections.length === 0 ? (
					<SkeletonRows />
				) : sections.length === 0 && !isLoading ? (
					<Text className="font-sans text-[12px] text-light-text-secondary dark:text-white/55 text-center px-sm py-lg m-0">
						{query.trim().length === 0
							? "Search for people to add to the group."
							: "No one matches that search."}
					</Text>
				) : (
					<View className="flex flex-col gap-3">
						{sections.map((section) => (
							<View key={section.letter} className="flex flex-col gap-1">
								<Text className="font-sans text-[12px] font-bold leading-[17px] text-[#6B7280] px-1">
									{section.letter}
								</Text>
								<View className="flex flex-col gap-[2px]">
									{section.users.map((u) => (
										<SelectableRow
											key={u.id}
											user={u}
											checked={selectedIds.has(u.id)}
											onPress={() => onToggle(u)}
										/>
									))}
								</View>
							</View>
						))}
					</View>
				)}
			</ScrollView>
		</>
	);
}

// Contact row with a trailing checkbox (Figma `contact-list-item` group).
function SelectableRow({
	user,
	checked,
	onPress,
}: {
	user: NewChatModalDirectoryUser;
	checked: boolean;
	onPress: () => void;
}): JSX.Element {
	return (
		<Pressable
			accessibilityRole="checkbox"
			accessibilityState={{ checked }}
			accessibilityLabel={user.displayName}
			onPress={onPress}
			className="w-full flex flex-row items-center gap-sm h-[45px] px-1 rounded-[8px] hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]"
		>
			<Avatar
				initials={user.displayName.charAt(0).toUpperCase() || "?"}
				{...(user.avatarUri !== undefined ? { imageUri: user.avatarUri } : {})}
				size="company"
				shape="rounded"
			/>
			<View className="flex-1 min-w-0 flex flex-col">
				<View className="flex flex-row items-center gap-1">
					<Text
						numberOfLines={1}
						className="font-sans text-[14px] leading-5 font-bold text-light-text-primary dark:text-[#F5F6F8]"
					>
						{user.displayName}
					</Text>
					{user.identityVerified === true ? <VerifiedBadge verified size={14} /> : null}
				</View>
				{(() => {
					const sub = [user.role, user.company].filter(Boolean).join(" · ");
					return sub.length > 0 ? (
						<Text
							numberOfLines={1}
							className="font-sans text-[12px] leading-[17px] text-light-text-secondary dark:text-[#B5BAC3]"
						>
							{sub}
						</Text>
					) : null;
				})()}
			</View>
			{/* Checkbox (18×18, r6). Checked = brand fill + white check. */}
			<View
				className={`w-[18px] h-[18px] rounded-[6px] items-center justify-center ${
					checked
						? "bg-[#537EFF]"
						: "border border-light-border-secondary dark:border-[#2F333B]"
				}`}
			>
				{checked ? <Check size={12} strokeWidth={3} color="#FFFFFF" /> : null}
			</View>
		</Pressable>
	);
}

// ── Step 2: group details (avatar + name + participants + folder) ───
function DetailsStep({
	groupTitle,
	onTitleChange,
	selected,
	selfName,
	selfAvatarUri,
	error,
	groupAvatarUri,
	onPickGroupAvatar,
}: {
	groupTitle: string;
	onTitleChange: (t: string) => void;
	selected: readonly NewChatModalDirectoryUser[];
	selfName: string;
	selfAvatarUri?: string;
	error: string | null;
	groupAvatarUri?: string;
	onPickGroupAvatar?: () => void;
}): JSX.Element {
	return (
		<ScrollView className="flex-1 min-h-0 px-[29px] pb-2">
			{/* Avatar picker + name. */}
			<View className="flex flex-row items-start gap-4 pt-1 pb-4">
				<Pressable
					accessibilityRole="button"
					accessibilityLabel="Add group photo"
					onPress={onPickGroupAvatar}
					className="relative w-[60px] h-[60px] rounded-[16px] items-center justify-center bg-light-surface-secondary dark:bg-[#15171C] border border-light-border-secondary dark:border-[#2F333B]"
				>
					{groupAvatarUri !== undefined ? (
						<Image
							source={{ uri: groupAvatarUri }}
							className="w-full h-full overflow-hidden rounded-[16px]"
							resizeMode="cover"
						/>
					) : (
						<NewChatIcon name="camera" size={20} color="#4B515C" />
					)}
					<View className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full items-center justify-center bg-[#537EFF]">
						<Plus size={12} strokeWidth={2.5} color="#FFFFFF" />
					</View>
				</Pressable>
				<View className="flex-1 gap-1">
					<View className="flex flex-row items-center gap-1">
						<Text className="font-sans text-[14px] leading-5 font-semibold text-light-text-primary dark:text-[#F5F6F8]">
							Group name
						</Text>
						<Text className="font-sans text-[14px] leading-5 text-light-text-secondary dark:text-[#6B7280]">
							(optional)
						</Text>
					</View>
					<View className="flex flex-row items-center h-[38px] px-3 gap-sm rounded-[8px] bg-light-surface-secondary dark:bg-[#111318]">
						<TextInput
							value={groupTitle}
							onChangeText={onTitleChange}
							placeholder="Name the group"
							placeholderTextColor="#6B7280"
							autoCapitalize="words"
							autoFocus
							maxLength={MAX_NAME}
							className="flex-1 font-sans text-[14px] text-light-text-primary dark:text-white"
							style={WEB_INPUT_OUTLINE_RESET}
						/>
						{groupTitle.length > 0 ? (
							<Pressable
								accessibilityRole="button"
								accessibilityLabel="Clear name"
								onPress={() => onTitleChange("")}
								className="w-[18px] h-[18px] items-center justify-center"
							>
								<X size={14} strokeWidth={2} color="#6B7280" />
							</Pressable>
						) : null}
					</View>
					<Text className="font-sans text-[12px] leading-[17px] text-light-text-secondary dark:text-[#6B7280]">
						{groupTitle.length}/{MAX_NAME}
					</Text>
				</View>
			</View>

			{/* Participants strip — includes YOU (the creator is always a member). */}
			<View className="gap-2 pb-4">
				<Text className="font-sans text-[12px] leading-[17px] text-light-text-secondary dark:text-[#B5BAC3]">
					{selected.length + 1} participant{selected.length + 1 === 1 ? "" : "s"}
				</Text>
				<ScrollView horizontal showsHorizontalScrollIndicator={false}>
					<View className="flex flex-row gap-3">
						<View className="w-10 items-center gap-1">
							<Avatar
								initials={selfName.charAt(0).toUpperCase() || "?"}
									{...(selfAvatarUri !== undefined ? { imageUri: selfAvatarUri } : {})}
								size="medium"
								shape="rounded"
							/>
							<Text
								numberOfLines={1}
								className="font-sans text-[12px] leading-5 text-light-text-secondary dark:text-[#B5BAC3] max-w-10"
							>
								You
							</Text>
						</View>
						{selected.map((u) => (
							<View key={u.id} className="w-10 items-center gap-1">
								<Avatar
									initials={u.displayName.charAt(0).toUpperCase() || "?"}
									{...(u.avatarUri !== undefined ? { imageUri: u.avatarUri } : {})}
									size="medium"
									shape="rounded"
								/>
								<Text
									numberOfLines={1}
									className="font-sans text-[12px] leading-5 text-light-text-secondary dark:text-[#B5BAC3] max-w-10"
								>
									{u.displayName.split(" ")[0]}
								</Text>
							</View>
						))}
					</View>
				</ScrollView>
			</View>

			{/* Add to folder (visual dropdown — folder assignment is a future
			    wire-up; the BE group-create takes title + members only). */}
			<Pressable
				accessibilityRole="button"
				accessibilityLabel="Add to folder"
				className="flex flex-row items-center h-9 px-3 gap-sm rounded-[10px] bg-light-surface-secondary dark:bg-[#111318]"
			>
				<NewChatIcon name="folder" size={18} color="#B5BAC3" />
				<Text className="flex-1 font-sans text-[14px] leading-5 text-light-text-primary dark:text-[#E8EAED]">
					Add to folder
				</Text>
				<ChevronRight size={18} strokeWidth={2} color="#6B7280" />
			</Pressable>

			{error !== null && (
				<Text className="font-sans text-[12px] text-light-accent-rose py-sm m-0">{error}</Text>
			)}
		</ScrollView>
	);
}

function SkeletonRows(): JSX.Element {
	return (
		<View className="flex flex-col gap-[2px]">
			{Array.from({ length: 5 }).map((_, i) => (
				<View
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton index is fine
					key={i}
					className="flex flex-row items-center gap-sm h-[45px] px-1"
				>
					<View className="w-7 h-7 rounded-[6px] bg-light-surface-tertiary dark:bg-white/[0.06]" />
					<View className="flex-1 flex flex-col gap-1">
						<View className="h-3 w-2/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
						<View className="h-3 w-3/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
					</View>
				</View>
			))}
		</View>
	);
}
